import statsig from './statsig.js';

/**
 * Apply Statsig experiments to elements with statsig-exp--{name}--{variant} classes.
 * Supports multi-variant experiments (A/B/C/D/etc.)
 */
function applyExperiments() {
  const elements = document.querySelectorAll('[class*="statsig-exp--"]');
  if (!elements.length) {
    return;
  }

  // Group elements by experiment name
  const experiments = new Map();

  elements.forEach(el => {
    const expClass = [...el.classList].find(c => c.startsWith('statsig-exp--'));
    if (!expClass) {
      return;
    }

    const parts = expClass.split('--');
    if (parts.length !== 3) {
      // eslint-disable-next-line no-console
      console.warn('Statsig: Invalid experiment class format:', expClass);
      return;
    }

    const [, expName, variant] = parts;

    if (!experiments.has(expName)) {
      experiments.set(expName, []);
    }
    experiments.get(expName).push({ el, variant });
  });

  // Apply visibility based on user's assignment
  experiments.forEach((variants, expName) => {
    const assignedVariant =
      statsig.getExperiment(expName).groupName || 'control';

    variants.forEach(({ el, variant }) => {
      const isMatch = variant === assignedVariant;
      el.classList.toggle('statsig-show', isMatch);
      el.classList.toggle('statsig-hide', !isMatch);
    });
  });
}

/**
 * Apply Statsig gates to elements with statsig-gate--{name}--{on|off} classes.
 */
function applyGates() {
  const elements = document.querySelectorAll('[class*="statsig-gate--"]');
  if (!elements.length) {
    return;
  }

  // Group elements by gate name
  const gates = new Map();

  elements.forEach(el => {
    const gateClass = [...el.classList].find(c =>
      c.startsWith('statsig-gate--'),
    );
    if (!gateClass) {
      return;
    }

    const parts = gateClass.split('--');
    if (parts.length !== 3) {
      // eslint-disable-next-line no-console
      console.warn('Statsig: Invalid gate class format:', gateClass);
      return;
    }

    const [, gateName, state] = parts;
    if (state !== 'on' && state !== 'off') {
      // eslint-disable-next-line no-console
      console.warn('Statsig: Gate state must be "on" or "off":', gateClass);
      return;
    }

    if (!gates.has(gateName)) {
      gates.set(gateName, []);
    }
    gates.get(gateName).push({ el, state });
  });

  // Apply visibility based on gate value
  gates.forEach((variants, gateName) => {
    const activeState = statsig.checkGate(gateName) ? 'on' : 'off';

    variants.forEach(({ el, state }) => {
      const isMatch = state === activeState;
      el.classList.toggle('statsig-show', isMatch);
      el.classList.toggle('statsig-hide', !isMatch);
    });
  });
}

/**
 * Initialize Statsig and apply all variant handling.
 * Call this before any tracking to ensure variants are visible.
 */
export async function initStatsigVariants() {
  await statsig.initialize();

  applyExperiments();
  applyGates();

  // Mark body as ready (disables CSS fallback animations)
  document.body.classList.add('statsig-ready');
}
